import React, { useEffect, useRef } from "react";
import { Box, Tabs, Tab } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { AttachMoney as AttachMoneyIcon } from "../icons";
import { Event as EventIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { Flight as FlightIcon } from "../icons";
import { T } from "./Translate";
import { brandColors, barColors } from "../theme";

const EDIT_SECTIONS = [
  { path: "/editOffers", Icon: AttachMoneyIcon, label: "Editar ofertas" },
  { path: "/editEvents", Icon: EventIcon, label: "Editar eventos" },
  { path: "/editCars", Icon: DirectionsCarIcon, label: "Editar autos" },
  { path: "/editTrips", Icon: FlightIcon, label: "Editar viajes" },
];
const EDIT_SECTION_PATHS = EDIT_SECTIONS.map((section) => section.path);

// Barra de pestañas compartida por editOffers.js / editEvents.js / editCars.js
// / editTrips.js — antes estaba copiada en cada archivo, se extrajo acá para
// no repetirla una tercera y cuarta vez.
const EditSectionTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = EDIT_SECTION_PATHS.includes(location.pathname)
    ? location.pathname
    : "/editOffers";

  // MUI desplaza la pestaña seleccionada a la vista al montar, pero mide antes
  // de que terminen de cargar la fuente y las traducciones (<T>): cuando los
  // textos cambian de ancho, la seleccionada quedaba cortada contra la flecha
  // (p. ej. "Editar autos" en celular). Se vuelve a centrar cada vez que la
  // fila de pestañas cambia de tamaño.
  const tabsRef = useRef(null);
  useEffect(() => {
    const root = tabsRef.current;
    const scroller = root?.querySelector(".MuiTabs-scroller");
    const row = root?.querySelector(".MuiTabs-flexContainer");
    if (!scroller || !row) return undefined;
    const revealSelected = () => {
      const selected = row.querySelector(".Mui-selected");
      if (!selected) return;
      const box = scroller.getBoundingClientRect();
      const tab = selected.getBoundingClientRect();
      if (tab.left < box.left) scroller.scrollLeft -= box.left - tab.left;
      else if (tab.right > box.right) scroller.scrollLeft += tab.right - box.right;
    };
    revealSelected();
    const observer = new ResizeObserver(revealSelected);
    observer.observe(row);
    observer.observe(scroller);
    // La animación de scroll de MUI (~300ms) pisa el ajuste inicial con su
    // medición vieja; se corrige de nuevo cuando termina.
    const timer = setTimeout(revealSelected, 400);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [activeSection]);

  return (
    <Box sx={{ borderTop: `1px solid ${barColors.border}` }}>
      <Tabs
        ref={tabsRef}
        value={activeSection}
        onChange={(e, value) => navigate(value)}
        // Scrollable con flechas: en celular las 4 pestañas no entran en el
        // ancho y con `centered` quedaban cortadas sin forma de llegar a ellas.
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 42,
          width: "100%",
          maxWidth: { xs: "100%", md: "980px" },
          mx: "auto",
          // Centradas cuando entran (reemplaza `centered`, que no se puede
          // combinar con scrollable). Con el scroller en flex, los márgenes
          // auto valen 0 cuando las pestañas desbordan, así que no quedan
          // cortadas a los costados sin poder llegar con las flechas.
          "& .MuiTabs-scroller": {
            display: "flex",
          },
          "& .MuiTabs-flexContainer": {
            mx: "auto",
          },
          "& .MuiTabs-scrollButtons": {
            color: barColors.text,
          },
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
          "& .MuiTab-root": {
            minHeight: 42,
            textTransform: "none",
            fontSize: "0.8rem",
            color: "text.secondary",
            minWidth: "auto",
            padding: "6px 14px",
          },
          "& .Mui-selected": {
            color: `${brandColors.textPrimary} !important`,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: brandColors.accent,
            height: "3px",
            borderRadius: "3px",
          },
        }}
      >
        {EDIT_SECTIONS.map(({ path, Icon, label }) => (
          <Tab
            key={path}
            value={path}
            icon={<Icon fontSize="small" />}
            iconPosition="start"
            label={<T>{label}</T>}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default EditSectionTabs;
